import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { moveItem, reorderTarget } from './reorderMath';

/**
 * Reorder a list by touch (docs/mobile-editor-layout-spec.md 11.5): a long
 * press on a card's handle lifts the card, it follows the finger while the
 * others slide aside to show where it will land, the list scrolls when the
 * finger nears its edges, and the drop hands the new order back. HTML5 drag
 * and drop, which the desktop uses, never fires for a finger on iOS.
 *
 * Mark each card with `data-reorder-id` and the list with
 * `data-reorder-list`; spread `handleProps(id)` on the handle and
 * `cardStyle(id)` on the card.
 */
const LIFT_MS = 300;
const SLOP = 8;
const EDGE = 56;
const GAP = 8;

interface Lift { id: string; from: number; over: number; dy: number; h: number }

function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

export function useTouchReorder<T extends { id: string }>(items: T[], onReorder: (next: T[]) => void, enabled: boolean) {
  const [lift, setLift] = useState<Lift | null>(null);
  const [dropped, setDropped] = useState<string | null>(null);
  const s = useRef<{
    pid: number; id: string; startY: number; lastY: number; timer: ReturnType<typeof setTimeout> | null;
    lifted: boolean; mids: number[]; from: number; h: number; scroller: HTMLElement | null; scroll0: number; raf: number;
    handle: HTMLElement | null;
  } | null>(null);
  const itemsRef = useRef(items);
  const onReorderRef = useRef(onReorder);
  useEffect(() => { itemsRef.current = items; onReorderRef.current = onReorder; });

  const scrollTop = (sc: HTMLElement | null) => (sc ? sc.scrollTop : window.scrollY);

  const recompute = useCallback(() => {
    const d = s.current;
    if (!d || !d.lifted) return;
    const dy = (d.lastY + scrollTop(d.scroller)) - (d.startY + d.scroll0);
    const over = reorderTarget(d.mids, d.from, d.mids[d.from] + dy);
    setLift({ id: d.id, from: d.from, over, dy, h: d.h });
  }, []);

  const end = useCallback((commit: boolean) => {
    const d = s.current;
    if (!d) return;
    if (d.timer) clearTimeout(d.timer);
    if (d.raf) cancelAnimationFrame(d.raf);
    s.current = null;
    if (!d.lifted) return;
    const dy = (d.lastY + scrollTop(d.scroller)) - (d.startY + d.scroll0);
    const over = reorderTarget(d.mids, d.from, d.mids[d.from] + dy);
    setLift(null);
    if (commit && over !== d.from) {
      onReorderRef.current(moveItem(itemsRef.current, d.from, over));
      setDropped(d.id);
      setTimeout(() => setDropped((x) => (x === d.id ? null : x)), 600);
    }
  }, []);

  useEffect(() => () => { const d = s.current; if (d?.timer) clearTimeout(d.timer); if (d?.raf) cancelAnimationFrame(d.raf); }, []);

  const handleProps = (id: string) => (!enabled ? {} : {
    'data-reorder-handle': id,
    style: { touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' } as CSSProperties,
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === 'mouse') return;
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const d = { pid: e.pointerId, id, startY: e.clientY, lastY: e.clientY, timer: null as ReturnType<typeof setTimeout> | null, lifted: false, mids: [] as number[], from: 0, h: 0, scroller: null as HTMLElement | null, scroll0: 0, raf: 0, handle };
      s.current = d;
      d.timer = setTimeout(() => {
        const list = handle.closest('[data-reorder-list]');
        const cards = list ? Array.from(list.querySelectorAll<HTMLElement>('[data-reorder-id]')) : [];
        const from = cards.findIndex((c) => c.dataset.reorderId === id);
        if (from < 0) { s.current = null; return; }
        d.scroller = scrollerOf(cards[from]);
        d.scroll0 = scrollTop(d.scroller);
        d.mids = cards.map((c) => { const r = c.getBoundingClientRect(); return r.top + r.height / 2 + d.scroll0; });
        d.from = from;
        d.h = cards[from].getBoundingClientRect().height;
        d.lifted = true;
        setLift({ id, from, over: from, dy: 0, h: d.h });
        /* Scroll the list while the finger sits near its top or bottom edge. */
        const loop = () => {
          if (s.current !== d || !d.lifted) return;
          const top = d.scroller ? d.scroller.getBoundingClientRect().top : 0;
          const bottom = d.scroller ? d.scroller.getBoundingClientRect().bottom : window.innerHeight;
          let v = 0;
          if (d.lastY < top + EDGE) v = -Math.ceil((top + EDGE - d.lastY) / 4);
          else if (d.lastY > bottom - EDGE) v = Math.ceil((d.lastY - (bottom - EDGE)) / 4);
          if (v) {
            if (d.scroller) d.scroller.scrollTop += v; else window.scrollBy(0, v);
            recompute();
          }
          d.raf = requestAnimationFrame(loop);
        };
        d.raf = requestAnimationFrame(loop);
      }, LIFT_MS);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const d = s.current;
      if (!d || e.pointerId !== d.pid) return;
      d.lastY = e.clientY;
      if (!d.lifted) {
        if (Math.abs(e.clientY - d.startY) > SLOP) end(false);
        return;
      }
      recompute();
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => { if (s.current?.pid === e.pointerId) end(true); },
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => { if (s.current?.pid === e.pointerId) end(false); },
  });

  /** The card's lift, its slide aside, or the drop's brief ring. */
  const cardStyle = (id: string): CSSProperties => {
    if (!lift) return dropped === id ? { boxShadow: '0 0 0 2px var(--color-biscayne)', transition: 'box-shadow 300ms ease' } : {};
    const index = itemsRef.current.findIndex((x) => x.id === id);
    if (id === lift.id) {
      return { transform: `translateY(${lift.dy}px) scale(1.02)`, position: 'relative', zIndex: 20, boxShadow: '0 12px 28px rgba(0,0,0,0.45)', transition: 'box-shadow 150ms ease' };
    }
    const shift = lift.h + GAP;
    if (lift.from < lift.over && index > lift.from && index <= lift.over) return { transform: `translateY(${-shift}px)`, transition: 'transform 150ms ease' };
    if (lift.from > lift.over && index >= lift.over && index < lift.from) return { transform: `translateY(${shift}px)`, transition: 'transform 150ms ease' };
    return { transform: 'translateY(0)', transition: 'transform 150ms ease' };
  };

  return { handleProps, cardStyle, lifting: lift?.id ?? null };
}
